import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Container,
  CircularProgress,
  Paper,
  Button,
  Card,
  CardContent,
  CardActions,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert
} from '@mui/material';
import {
  Groups as GroupsIcon,
  Add as AddIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/layout/MainLayout';
import { getAllTeams, createTeam, getMyTeams } from '../../services/teamService';
import { Team, CreateTeamData } from '../../types/team';

function TeamsPage() {
  const [teams, setTeams] = useState<Team[]>([]);
  const [myTeams, setMyTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newTeamData, setNewTeamData] = useState<CreateTeamData>({
    name: '',
    description: '',
    maxMembers: 10
  });
  const [error, setError] = useState<string>('');
  const navigate = useNavigate();

  useEffect(() => {
    loadTeams();
  }, []);

  const loadTeams = async () => {
    setLoading(true);
    try {
      const [allTeamsResponse, userTeams] = await Promise.all([
        getAllTeams(1, 50),
        getMyTeams()
      ]);
      setTeams(allTeamsResponse.teams);
      setMyTeams(userTeams);
    } catch (err) {
      console.error('Error loading teams:', err);
      setError('Failed to load teams');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTeam = async () => {
    if (!newTeamData.name.trim()) {
      setError('Team name is required');
      return;
    }

    try {
      await createTeam(newTeamData);
      setCreateDialogOpen(false);
      setNewTeamData({ name: '', description: '', maxMembers: 10 });
      loadTeams();
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      setError(error.response?.data?.message || 'Failed to create team');
    }
  };

  const isUserInTeam = (teamId: number) => {
    return myTeams.some(t => t.id === teamId);
  };

  if (loading) {
    return (
      <MainLayout>
        <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Container>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Typography variant="h4" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GroupsIcon sx={{ fontSize: 40 }} />
            Teams
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Create Team
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* My Teams Section */}
        {myTeams.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              My Teams
            </Typography>
            <Grid container spacing={3}>
              {myTeams.map((team) => (
                <Grid item xs={12} sm={6} md={4} key={team.id}>
                  <Card elevation={3}>
                    <CardContent>
                      <Typography variant="h6" fontWeight="bold" gutterBottom>
                        {team.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {team.description || 'No description'}
                      </Typography>
                      <Chip
                        label="Member"
                        size="small"
                        color="primary"
                        sx={{ mr: 1 }}
                      />
                    </CardContent>
                    <CardActions>
                      <Button size="small" onClick={() => navigate(`/student/teams/${team.id}`)}>
                        View Team
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* All Teams Section */}
        <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
          All Teams
        </Typography>
        <Grid container spacing={3}>
          {teams.map((team) => (
            <Grid item xs={12} sm={6} md={4} key={team.id}>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    {team.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {team.description || 'No description'}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon fontSize="small" />
                    <Typography variant="caption">
                      Max {team.maxMembers} members
                    </Typography>
                  </Box>
                </CardContent>
                <CardActions>
                  <Button
                    size="small"
                    variant={isUserInTeam(team.id) ? 'outlined' : 'contained'}
                    onClick={() => navigate(`/student/teams/${team.id}`)}
                  >
                    {isUserInTeam(team.id) ? 'View Team' : 'Join Team'}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>

        {teams.length === 0 && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              No teams available. Create one to get started!
            </Typography>
          </Paper>
        )}

        {/* Create Team Dialog */}
        <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Create New Team</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Team Name"
              type="text"
              fullWidth
              variant="outlined"
              value={newTeamData.name}
              onChange={(e) => setNewTeamData({ ...newTeamData, name: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Description"
              type="text"
              fullWidth
              variant="outlined"
              multiline
              rows={3}
              value={newTeamData.description}
              onChange={(e) => setNewTeamData({ ...newTeamData, description: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Max Members"
              type="number"
              fullWidth
              variant="outlined"
              value={newTeamData.maxMembers}
              onChange={(e) => setNewTeamData({ ...newTeamData, maxMembers: parseInt(e.target.value) })}
              inputProps={{ min: 2, max: 50 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateTeam} variant="contained">Create</Button>
          </DialogActions>
        </Dialog>
      </Container>
    </MainLayout>
  );
}

export default TeamsPage;
